import { Asterisk } from "./styles";
import { color } from "@styles";
import { mobile } from "components/templates/Breakpoints";
import styled from "styled-components";
import React, { TextareaHTMLAttributes, useCallback, useEffect, useRef, useState } from "react";
import { useField } from "@unform/core";

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  name: string;
  isRequired?: boolean;
  inputHeight?: string;
}

export const TextArea = ({
  placeholder,
  isRequired = true,
  inputHeight,
  ...rest
}: TextAreaProps) => {
  const inputRef = useRef(null);

  const { fieldName, registerField, error, clearError } = useField(rest.name);
  const [hasPlaceHolder, setHasPlaceHolder] = useState(true);

  const handlePlaceHolder = useCallback(
    (value) => {
      if (value && hasPlaceHolder) {
        setHasPlaceHolder(false);
      }
      if (!value && !hasPlaceHolder) {
        setHasPlaceHolder(true);
      }
    },
    [hasPlaceHolder],
  );

  const handleClearError = (e: React.FocusEvent<HTMLTextAreaElement, Element>) => {
    if (e.currentTarget.value) {
      clearError();
    }
  };

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef,
      getValue: (ref) => {
        return ref.current.value;
      },
      setValue: (ref, value) => {
        ref.current.value = value;
      },
      clearValue: (ref) => {
        ref.current.value = "";
      },
    });
  }, [fieldName, registerField]);

  return (
    <Relative>
      <StyledInput
        $inputHeight={inputHeight}
        onBlur={handleClearError}
        $hasError={!!error}
        onChange={(e) => handlePlaceHolder(e.currentTarget.value)}
        ref={inputRef}
        {...rest}
      />
      <InputLabelWrapper>
        <InputLabel htmlFor={rest.name}>
          {!hasPlaceHolder ? null : (
            <>
              {placeholder}
              {isRequired && <Asterisk />}
            </>
          )}
        </InputLabel>
      </InputLabelWrapper>
      {!!error ? <ErrorText>{error}</ErrorText> : null}
    </Relative>
  );
};

const Relative = styled.div`
  position: relative;
  align-self: flex-start;
  width: 100%;
`;

const ErrorText = styled.div`
  color: #ff5f5f;
  margin: -0.5rem 0 0.5rem 0;
`;

const InputLabelWrapper = styled.div<{ invisible?: boolean }>`
  width: 100%;
  max-width: calc(100% - 2.5rem);
  min-height: 3.25rem;
  position: absolute;
  top: 1.4rem;
  left: 1.375rem;
  display: flex;
  align-items: center;
  pointer-events: none;
  visibility: ${(p) => (p.invisible ? "hidden" : "visible")};

  ${mobile} {
    top: 0.9rem;
  }
`;

const InputLabel = styled.label<{ invisible?: boolean }>`
  visibility: ${(p) => (p.invisible ? "hidden" : "visible")};
  color: ${color.B2100};
  font-weight: normal;
  font-size: 1.25rem;
  pointer-events: none;
`;

const StyledInput = styled.textarea<{ $hasError?: boolean; $inputHeight?: string }>`
  width: 100%;
  height: ${(p) => (p.$inputHeight ? p.$inputHeight : "8rem")};
  margin: 1rem 0;
  box-sizing: border-box;
  padding: 1rem 1.25rem;
  font-weight: normal;
  font-size: 1.25rem;
  color: ${color.B1100};
  border-radius: 0.5rem;
  border: 2px solid ${(p) => (p.$hasError ? "#FF5F5F" : color.B240)};
  font-family:
    "__LotaGrotesque_33d198",
    "__LotaGrotesque_Fallback_33d198",
    --apple-system,
    BlinkMacSystemFont,
    Segoe UI,
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    Open Sans,
    Helvetica Neue,
    sans-serif;
  resize: none;

  ${mobile} {
    margin: 0.5rem 0;
  }

  ::placeholder {
    color: ${color.B2100};
  }
`;
